import {
  afterNextRender,
  AfterViewInit,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from "@angular/common";
import { FooterComponent } from "../shared/footer/footer.component";
import { SignalsStoreService } from '../shared/signals-store.service';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '../../environments/environment';
import { RouterLink } from '@angular/router';
import { LocalStorageService } from '../shared/local-storage.service';
import { LOCALSTORAGE_KEYS } from '../shared/constants/databases';
import { ModalContentService } from '../shared/modal-content/modal-content.service';
import { ModalContentTypes } from '../shared/constants/modal-content-types';
import { KlaviyoFormsComponent } from '../shared/klaviyo/forms/klaviyo-forms.component';

@Component({
  selector: 'app-home',
  imports: [CommonModule, FooterComponent, RouterLink, KlaviyoFormsComponent],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  host: { 'class': 'home-page' }
})
export class HomeComponent implements OnInit, OnDestroy, AfterViewInit {

  //#region Services

  #signalsStoreService = inject(SignalsStoreService);

  #localStorageService = inject(LocalStorageService);

  #modalContentService = inject(ModalContentService);

  #meta = inject(Meta);

  #title = inject(Title);

  //#endregion

  //#region Properties

  hasGlobalMessages = computed(() => !!this.#signalsStoreService.globalMessages().length);

  hasSession = computed(() => this.#signalsStoreService.hasSession());

  isSessionLoaded = computed(() => this.#signalsStoreService.isSessionLoaded());

  testimonials = signal([
    {
      author: 'Emily Westerbeck',
      testimony: "Our dog, Reese, has been enjoying the food for a while. She loves it, including the sweet potato treats. The delivery service is fantastic. We also got the calming balm recently, and it's great. We're sticking with this brand."
    },
    {
      author: 'Sarah L.',
      testimony: "absolutely love this pet store! The staff is super friendly and knowledgeable. I bought a new bed for my dog, and he loves it! The quality is amazing, and the prices are fair. I’ll definitely be coming back for more pet supplies"
    },
    {
      author: 'Jason M.',
      testimony: "Great experience shopping here! They have a wide variety of pet food, and I found the perfect grain-free option for my cat. The customer service was excellent, and they even gave me a free sample to try. Highly recommend!"
    },
  ]);

  whyChooseUs = signal([
    {
      icon: '1.svg',
      title: 'Premium Recipes',
      text: 'Premium and super-premium holistic, natural, and organic recipe pet foods.'
    },
    {
      icon: '2.svg',
      title: 'Slow-cooking method',
      text: 'Our unique slow cooking method has been proven to retain the dense nutrition pet wants is known for.'
    },
    {
      icon: '3.svg',
      title: 'No middlemen',
      text: 'There are no middlemen, we do not buy from distributors, and we sell and deliver only what you need, by the pound.'
    },
    {
      icon: '4.svg',
      title: 'Super-premium ingredients',
      text: 'Pet Wants superiority is in the density of our available nutrients. Our super-premium ingredients are still super-premium when your pet eats the food.'
    },
    {
      icon: '5.svg',
      title: 'From Us to You',
      text: "Pet Wants brand foods are never warehoused. Pet wants food comes directly to to us upon being batch cooked. It's never Warehoused or stored outside of our facility here in Cincinnati."
    },
    {
      icon: '6.svg',
      title: 'High-quality meat',
      text: 'We are industry experts in dry extruded high meat, natural formulations including specialized ingredients and processes. You won’t find a higher quality food to give your pet, giving you the peace of mind you both deserve!'
    }
  ]);

  howItWork = signal([
    {
      icon: '1.svg',
      title: 'Place a Customized Order',
      text: "Select a pet food formula made fresh each month and any treats, toys or nutritional products from our selection of house-made and curated goods."
    },
    {
      icon: '2.svg',
      title: 'Schedule a Delivery or Pick Up',
      text: "Select a delivery schedule of how often you would like receive your order. The recurring option is the easiest way to ensure you never run out of FRESH pet food and may save you a trip to our store!"
    },
    {
      icon: '3.svg',
      title: 'Feed Fresh Food',
      text: "Based on your address, you will be assigned to a specific delivery day, and can make changes to your order up until the day before your order arrives."
    }
  ]);

  faqs = signal([
    {
      question: 'Why should I feed fresh pet food?',
      answer: `
        <span>
          Fresh food has a higher nutritional content, and since there is nothing to preserve vitamins and minerals, the fresher the food, the more nutritional for your pet. We start with fresh food like salmon, duck, whitefish, and chicken, premium protein sources meant for consumption by your dog and/or cat. Along with eliminating corn, soy, and wheat products, we also eliminate all fillers and animal by-products in our complete and balanced recipes.
        </span>
        <br/><br/>
        <span>
          Fresh is Better- Pet Wants delivers natural and fresh food to your pet’s bowl within weeks of being made and at the height of its nutritional value. Our food is made fresh in small batches and slow-cooked to lock in the nutrients. Unlike ours, most mass-produced commercial pet food is manufactured, packaged, and shipped to a series of distributors across the nation where it could sit on shelves 12-18 months losing its nutritional value...even before it gets to the retail store shelves sitting some more. The shorter the shelf life, the better the nutrition. We make our food fresh each month and sell it by the pound so you can get just the right amount for your pets.
        </span>
      `
    },
    {
      question: 'What kind of ingredients do you use in your recipes?',
      answer: `
        <span>
          Dogs and cats are carnivores by nature, and their diet should reflect their natural foods as closely as possible. At Pet Wants, we swim against the mainstream tide and avoid ingredients which contribute to pet allergies and health issues. We never use corn, soy, wheat, sugar, artificial colors, animal by-products or fillers.
        </span>
        <br/><br/>
        <span>
          We control what goes in our product and source all USA ingredients, with the exception of our lamb which is sourced in New Zealand and our salmon seasonally sourced in Nova Scotia or off the coast of Chile. All Pet Wants foods are designed to meet or exceed the nutritional levels established by the Association of American Feed Control (AAFCO) nutrient profiles for “All Life Stages.”
        </span>
        <br/><br/>
        <span>
         If you’re not sure what the ingredients are in your current pet food, you’re not alone. Start by contacting us and we will be happy to show you the ingredients in our recipes and compare that to what you are feeding now. You can find the specific ingredients lists for our recipes here: ________________.
        </span>
      `
    },
    {
      question: 'My dog scratches a lot and licks his paws, can you help with that?',
      answer: `
        <span>
          Yes we can! Chances are your pet might have an allergy or a food sensitivity. The most common forms of pet food allergies are related to CORN, SOY or WHEAT. Pet Wants never uses corn, soy or wheat (or any in any animal by-products, like guts, eyes, and beaks either) in any of our formulas! Pet Wants begins with the freshest, highest quality ingredients available. Then, we expertly craft complete and balanced foods for your dog and cat, treats, supplements, and pet care products to match your pet’s activity level, age, food preferences and sensitivity. Contact us for a free sample or to discuss pet nutrition further. 513-888-3647
        </span>
      `
    },
    {
      question: 'Do you have special formulas for pet with allergies?',
      answer: `
        <span>
          All of our recipes are formulated free from common food allergens and sensitivity triggers. Our food is made fresh, to order, and slow cooked in small batches at low temperatures to lock in nutrients. Also, we never use corn, wheat, soy, animal by-products, or artificial preservatives. We only source and use the best ingredients and deliver it fresh, within weeks of being made, at the height of its optimal freshness and nutritional value. As a result, our food is very easy on the pet’s digestive system and they are getting all the nutrients, vitamins and minerals your pet needs. And this is critical. For example, if your pet is eating stale food that is deficient in nutrients, their organs can suffer. And the largest organ, their skin, if deprived of sufficient nutrients can become unhealthy and itchy. Not a case of an allergy, just a case of inferior food. Rest assured, with Pet Wants, we offer only high-quality food, treats, and supplements to ensure pets live the happiest, healthiest, and longest lives possible.
        </span>
      `
    },
    {
      question: 'Does my pet have food allergies, environmental allergies or food insensitivities?',
      answer: `
        <span>
          Food intolerances and sensitivities, while not a true allergy, are quite common and can cause similar issues like inflammation, excess bloating and gas, stomach irritation, itching, and general discomfort. Intolerances are usually caused by specific ingredients (e.g., corn, wheat, low-quality meats, animal by-products, etc.), how the food is processed (high temperatures), food additives, artificial preservatives, and colors. Many of these common sensitivity triggers are caused by mass-produced pet food, which can also exacerbate irritable bowel syndrome.
        </span>
        <br/><br/>
        <span>
          Talk to us about Gut Health. It's our experience that poor gut health and what are often perceived as allergies can be associated with each other and are intertwined.
        </span>
        <br/><br/>
        <span>
          Pet Wants Food Tips: So, whether your pet suffers from food allergies or food intolerances/sensitivities, at Pet Wants, our natural, high-quality and fresh food is free from common food allergens and sensitivity triggers. Our food is made fresh, to order, and slow cooked in small batches at low temperatures to lock in nutrients. Also, we never use corn, wheat, soy, animal by-products, or artificial preservatives. Also, we only source and use the best ingredients and deliver it fresh, within weeks of being made, at the height of its optimal freshness and nutritional value. As a result, our food is very easy on the pet’s digestive system and they are getting all the nutrients, vitamins and minerals your pet needs. And this is critical. For example, if your pet is eating stale food that is deficient in nutrients, their organs can suffer. And the largest organ, their skin, if deprived of sufficient nutrients can become unhealthy and itchy. Not a case of an allergy, just a case of inferior food. Rest assured, with Pet Wants, we offer only high-quality food, treats, and supplements to ensure pets live the happiest, healthiest, and longest lives possible.
        </span>
        <br/><br/>
        <span>
          Environmental Triggers: Symptoms of environmental allergies can also be itchy skin that causes them to lick, bite, or chew their paws and scratch their ears and rear. Environmental allergies can be caused by factors such as pollen, grass, dust, and mold. First, identify if the irritating symptoms are affected by the seasons. Very often, if the symptoms get worse in the spring and/or fall, the issues are most likely related to plants as they bloom and release pollen. Too much rain in the Spring can cause mold spores and irritation. Fresh, natural and healthy Pet Wants food can help boost your pet’s health and immune system to fight any environmental triggers. Regular baths, ear cleanings and increasing the amount of Omega 3 fatty acids in your pet’s diet (promotes anti-inflammatory immune responses) can help with seasonal allergy issues as well. If the issues are chronic it may be year-round environmental triggers such as dust, or dust mites, which need to be treated continuously.
        </span>
        <br/><br/>
        <span>
          Always consult with your veterinarian on food allergies or environmental allergies.
        </span>
        <br/><br/>
        <strong>
          HOW CAN Pet Wants HELP?
        </strong>
        <br/><br/>
        <span>
          Consult with a Pet Wants staff for a tailored recommendation on food, treats, and supplements to find potential solutions to these allergic and sensitivity issues. Our Pet Wants Nutrition Specialist will be happy to review your current food ingredients and compare that to our fresh and natural Pet Wants food ingredients.
        </span>
        <br/><br/>
        <span>
          As always, we want to earn your trust and be your Nutrition Specialists and problem solvers. Please call us with any of your pet’s issues or better yet, let’s arrange for a face-to-face consultation so we can deliver real solutions and relief.
        </span>
      `
    },
    {
      question: 'How much food should I order?',
      answer: `
        <span>
          There are approximately 4 cups of food per pound.
        </span>
        <br/><br/>
        <span>
          FOR DOGS: If you know how many cups of food to feed, you can multiply the number of cups per day by 7 then divide by 4 = total pounds per week. You can also use our Dog Food Calculator too to help guide you on this. INSERT CALCULTOR HERE
        </span>
        <br/><br/>
        <span>
          FOR CATS: On average an adult cat eats about 1 pound per week.
        </span>
        <br/><br/>
        <span>
          Daily Feeding Guidelines
        </span>
        <br/><br/>
        <span>
          Weight of Dog (lbs.) Cups/Day*
        </span>
        <br/><br/>
        <span>
          Up to 10 1/3 - 2/3
        </span>
        <br/><br/>
        <span>
          11 to 30 2/3 - 1 1/3
        </span>
        <br/><br/>
        <span>
          31 to 50 1 1/3 - 2 1/4
        </span>
        <br/><br/>
        <span>
          51 to 70 2 1/4 - 3 1/4
        </span>
        <br/><br/>
        <span>
          71 - 80 3 1/4 - 4 1/8
        </span>
        <br/><br/>
        <span>
          Over 80 Add ½ cup for each additional 15 lbs. of body weight.
        </span>
        <br/><br/>
        <small>
          *These are quantity suggestions; the amount you feed your pet can vary, depending greatly upon the breed, activity level, metabolism and your preferences.
        </small>
      `
    },
    {
      question: 'What if I am not home when my order arrives?',
      answer: `
        <span>
          You do not need to be home when we deliver. If you would like us to put your order in a specific location, you can add special delivery instructions on your account and we will accommodate accordingly. Feel free to have us call you first or text when we deliver too.
        </span>
      `
    }
  ]);

  homeImages = signal([
    {
      circle: '/assets/images/landing/home/hero/dog-mini.webp',
      main: '/assets/images/landing/home/hero/dog.webp',
      isSelected: true
    },
    {
      circle: '/assets/images/landing/home/hero/homeph1-mini.webp',
      main: '/assets/images/landing/home/hero/homeph1.webp',
      isSelected: false
    },
    {
      circle: '/assets/images/landing/home/hero/homeph2-mini.webp',
      main: '/assets/images/landing/home/hero/homeph2.webp',
      isSelected: false
    }
  ]);

  mainImage = signal('/assets/images/landing/home/hero/dog.webp');

  currentIndexFaqs = signal(0);

  currentIndexTestimony = signal(0);

  currentTestimony = signal<any>(this.testimonials()[0]);

  timerTestimonial: NodeJS.Timeout | null = null;

  timerImage: NodeJS.Timeout | null = null;

  //#endregion

  //#region Constructor

  constructor() {
    afterNextRender(() => {
      this.#initTestimonialSlider();
      this.#initImageSlider();
    });
  }

  ngAfterViewInit() {
    this.#goToSection();
  }

  ngOnInit() {
    this.#signalsStoreService.isInHomePage.set(true);
    this.#setUpSEOTags();
  }

  ngOnDestroy() {

    if (this.timerTestimonial)
      clearInterval(this.timerTestimonial);

    if (this.timerImage)
      clearInterval(this.timerImage);

    this.#signalsStoreService.isInHomePage.set(false);
  }

  //#endregion

  //#region Methods

  #setUpSEOTags() {

    const { mainTitle, mainDescription, baseKeywords: keywords, baseURL, type } = environment.config.seo;
    const url = 'home';

    // Page title
    this.#title.setTitle(mainTitle);

    // Standart <meta> tag for SEO
    this.#meta.addTag({ name: 'description', content: mainDescription });
    this.#meta.addTag({ name: 'keywords', content: keywords.join(', ') });

    // Open Graph for social networks:
    this.#meta.addTag({ property: 'og:title', content: mainTitle });
    this.#meta.addTag({ property: 'og:description', content: mainDescription });
    this.#meta.addTag({ property: 'og:url', content: `${baseURL}${url}` });
    this.#meta.addTag({ property: 'og:type', content: type });
  }

  #goToSection() {

    const link: string | null = this.#localStorageService.get(LOCALSTORAGE_KEYS.LINK_TO_GO_IN_HOME);

    if (!link)
      return;

    const divToScroll = document.getElementById(link);
    if (!divToScroll) return;

    setTimeout(() => {
      divToScroll.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.#localStorageService.remove(LOCALSTORAGE_KEYS.LINK_TO_GO_IN_HOME);
    }, 200);
  }

  prevNextTestimonial(index: number) {
    this.currentIndexTestimony.set(index);
    this.currentTestimony.set(this.testimonials()[index]);
    this.#initTestimonialSlider();
  }

  openFaq(index: number) {

    if (index === this.currentIndexFaqs())
      index = -1;

    this.currentIndexFaqs.set(index);
  }

  selectMainImage(item: any) {

    this.homeImages.update(val => {
      val.forEach(x => x.isSelected = false);
      return val;
    });

    item.isSelected = true;
    this.mainImage.set(item.main);
  }

  openModal() {

    this.#modalContentService
      .openModal(ModalContentTypes.SET_UP_DELIVERY_HERE);
  }

  #initTestimonialSlider() {
    if (this.timerTestimonial) {
      clearInterval(this.timerTestimonial);
      this.timerTestimonial = null;
    }

    this.timerTestimonial = setInterval(() => {
      const index = this.currentIndexTestimony() === (this.testimonials().length - 1);
      this.prevNextTestimonial(index ? 0 : (this.currentIndexTestimony() + 1));
    }, 1000 * 5);
  }

  #initImageSlider() {
    if (this.timerImage) {
      clearInterval(this.timerImage);
      this.timerImage = null;
    }

    this.timerImage = setInterval(() => {
      const selectedIndex = this.homeImages().findIndex(x => x.isSelected);
      const index = selectedIndex === (this.homeImages().length - 1) ? 0 : (selectedIndex + 1);
      this.selectMainImage(this.homeImages()[index]);
    }, 1000 * 5);
  }

  changeMainImage(item: any) {
    this.selectMainImage(item);
    this.#initImageSlider();
  }

  //#endregion
}
